/**
 * 路由权限
 * 对应 EB: view/mer/src/store/modules/permission.js
 */
import { constantRoutes, dynamicRoutes } from '@/router';
import { defineStore } from 'pinia';
import type { RouteRecordRaw } from 'vue-router';
import useUserStore from '@/store/modules/user';

type PermissionRoute = RouteRecordRaw & {
    permissions?: string[];
    roles?: string[];
    meta?: RouteRecordRaw['meta'] & { roles?: string[] };
};

/**
 * 对应 EB: hasPermission
 */
function hasPermission(roles: string[], route: RouteRecordRaw) {
    const metaRoles = (route as PermissionRoute).meta?.roles;
    if (metaRoles?.length) {
        return roles.some((role) => metaRoles.includes(role));
    }
    return true;
}

/**
 * 对应 EB: filterAsyncRoutes
 */
export function filterAsyncRoutes(routes: RouteRecordRaw[], roles: string[]) {
    const res: RouteRecordRaw[] = [];

    routes.forEach((route) => {
        const tmp = { ...route };
        if (hasPermission(roles, tmp)) {
            if (tmp.children) {
                tmp.children = filterAsyncRoutes(tmp.children, roles);
            }
            res.push(tmp);
        }
    });

    return res;
}

/** 带 permissions / roles 的动态路由过滤（tita：用 crmebAccess） */
export function filterDynamicRoutes(routes: RouteRecordRaw[]) {
    const userStore = useUserStore();
    const access = userStore.crmebAccess;
    const res: RouteRecordRaw[] = [];

    routes.forEach((route) => {
        const item = route as PermissionRoute;
        if (item.permissions?.length) {
            if (!access.length || item.permissions.some((p) => access.includes(p))) {
                res.push(route);
            }
            return;
        }
        if (item.roles?.length) {
            if (item.roles.some((role: string) => userStore.roles.includes(role))) {
                res.push(route);
            }
            return;
        }
        res.push(route);
    });

    return res;
}

const usePermissionStore = defineStore('permission', {
    state: (): {
        routes: RouteRecordRaw[];
        addRoutes: RouteRecordRaw[];
    } => ({
        routes: [],
        addRoutes: [],
    }),
    actions: {
        /** 对应 EB: SET_ROUTES */
        setRoutes(routes: RouteRecordRaw[]) {
            this.addRoutes = routes;
            this.routes = constantRoutes.concat(routes);
        },
        /**
         * 根据角色生成可访问路由
         * 对应 EB: permission/generateRoutes
         */
        generateRoutes(roles: string[]) {
            return new Promise<RouteRecordRaw[]>((resolve) => {
                import('@/router').then(({ default: routerInstance }) => {
                    let accessedRoutes: RouteRecordRaw[];
                    if (roles.includes('crmeb')) {
                        accessedRoutes = filterDynamicRoutes(dynamicRoutes);
                    } else {
                        accessedRoutes = filterAsyncRoutes(dynamicRoutes, roles);
                        accessedRoutes = filterDynamicRoutes(accessedRoutes);
                    }
                    accessedRoutes.forEach((route) => {
                        routerInstance.addRoute(route);
                    });
                    this.setRoutes(accessedRoutes);
                    resolve(accessedRoutes);
                });
            });
        },
        /**
         * EB 壳：静态路由已在 router 注册，补充带 permissions 的 dynamicRoutes
         * 对应 permission/generateRoutes + 商户 roles=['eb']
         */
        generateCrmebRoutes() {
            const userStore = useUserStore();
            const roles = userStore.roles.length ? userStore.roles : ['crmeb'];
            return this.generateRoutes(roles);
        },
    },
});

export default usePermissionStore;
